export const ELEMENTS = ['fire', 'water', 'grass', 'lightning', 'earth'] as const

import type { StarweaveRunState } from './starweave'

export type ElementKey = (typeof ELEMENTS)[number]

export type Stats = Record<ElementKey, number>

export interface CreatureDef {
  id: string
  name: string
  stats: Stats
  /** Innate travel speed (1 = heavy, 2 = typical, 3 = nimble). */
  speed: number
  /** v1.01: uncommon = the baseline 24; rare = the 🌟 Stardust roster
   * (elevated statlines, the only defs with innate perks). epic and
   * legendary are reserved for the creature expansion — they already
   * carry draft weights and a shop price (v2.75). */
  rarity?: 'uncommon' | 'rare' | 'epic' | 'legendary'
  /** Innate ⚔️ Fight points (v0.91 stat; rares only for now). */
  fight?: number
  /** 🧬 v2.80: this creature's OWN passive (a key in CREATURE_PASSIVES).
   * Replaces the elemental passive entirely — never both. */
  passive?: string
  /** 🎯 v2.80: this creature's OWN on-use skill (a key in
   * CREATURE_SKILLS), replacing Surge. */
  skill?: string
  /** One-line shop/codex flavor for the rare roster. */
  blurb?: string
  /** 🔋 Future creatures (v1.95, per Javi): no innate Surge — they
   * bring something else instead. Overcharge Cell lends them a normal
   * (undoubled) surge. */
  noSurge?: boolean
  /** 🏆 v6.88: never rolled, never sold - the ledger awakens it (Legacy,
   * the homage creatures). The codex shows only a tease until then. */
  awardOnly?: boolean
  /** 🎓 v6.88 (per Javi): level-up points may only land on these
   * elements (Mr. Khool trains 💧/🪨, Aid ⚡/🌿). Absent = any. */
  trainable?: ElementKey[]
}

export interface Creature {
  id: string
  defId: string
  name: string
  level: number
  xp: number
  stats: Stats
  /** Innate travel speed; only relics modify it. */
  speed: number
  /** ⚔️ Innate Fight stat (v0.91): each point shaves 4% off team fight
   * time (summed across the team, cap −70%). Baseline 0 for everyone —
   * passives, trained skills and stacks add to it dynamically. */
  fight?: number
  injured: boolean
  /** Injury stacks (Ascension 4 lets them pile up); 0/undefined = healthy.
   * `injured` stays the canonical boolean — keep both in sync via
   * injureCreature/cureCreatureStack in utils/battle.ts. */
  injuries?: number
  available: boolean
  pendingStatPoints: number
  assignedMissionId: string | null
  /** Innate surge skill armed: bonus applies to the next mission. */
  surgePrimed: boolean
  /** Seconds until the surge skill can be armed again (0 = ready). */
  surgeCooldown: number
  /** Trained skill ids earned at 🎓 Training missions (v0.79). */
  learnedSkills?: string[]
  /** 🧘 Equilibrium mode (v1.12): stats present as an even spread while
   * true; the underlying allocation is never rewritten, so the toggle
   * is fully reversible. */
  equalized?: boolean
  /** Seconds until Equilibrium can be toggled again (0 = ready). */
  equalizeCooldown?: number
  /** Seconds until each consumed armed skill can re-arm (v1.12). */
  armedSkillCooldowns?: Record<string, number>
  /** 🧘 Deep Sit (v2.80): seconds spent resting at home since its last
   * dispatch. Every 10 banks another slab of 🪨 for its next fight. */
  restSeconds?: number
  /** 💨 Momentum stacks (encounter-scoped; reset at node end / own failure). */
  momentumStacks?: number
  /** ⚓ Impulse stacks (encounter-scoped; reset at node end / own failure). */
  impulseStacks?: number
  /** Armed activated trained skills (tough-learner / pain-into-power);
   * consumed by the creature's next resolved mission, win or lose. */
  armedSkills?: string[]
  /** 😤 Grudge: primed by any team failure, spent on its next dispatch. */
  grudgePrimed?: boolean
  /** 📣 Rallied (v0.92): a Rallying Cry teammate waives this creature's
   * post-return surge cooldown. Cleared when it lands. */
  surgeRallied?: boolean
  /** 🌳 Deep Roots: seconds of home time banked toward the next XP tick. */
  deepRootsClock?: number
  /** 🎭 Understudy (v2.82): a passive copied from a teammate at node
   * end, REPLACING whatever this creature would otherwise awaken. */
  passiveOverride?: string
  /** 🎯 Creature skill (v2.86): a 'primed' skill is armed and waiting
   * for this creature's next dispatch, like a Surge. */
  skillPrimed?: boolean
  /** Seconds until its creature skill can fire again (0 = ready).
   * Above ONCE_PER_NODE means spent until the node ends. */
  skillCooldown?: number
  /** 🔥 Overheat (v2.86): benched for the rest of the node — it traded
   * its next mission for a huge one. */
  benched?: boolean
  /** 🌬️ Second Wind (v2.91): it came home from a mission; its next
   * dispatch this node carries the bonus. */
  secondWindPrimed?: boolean
  /** 🔋 Static Charge (v2.91): banked charges, spent by its next surge. */
  staticCharges?: number
  /** 🔋 T2+ (v3.07): a pending cooldown cut from spent charges. */
  staticCooldownCut?: number
  /** 🌱 Evergrowth (v2.91): level-ups not yet absorbed by a Rootwarden. */
  levelUpsUncounted?: number
  /** 🌱 Evergrowth Apotheosis: teammate level-ups toward its own free level. */
  evergrowthTicker?: number
  /** 🔷 Refract (v2.92): a copied statline riding until its next mission resolves. */
  refractStats?: Stats
  /** 🏮 Will-o'-Wisp (v2.92): banked ghosts from expired missions. */
  wispGhosts?: number
  /** 🤭 Infectious Giggle (v2.92): the element and bonus riding this creature. */
  giggleElement?: ElementKey
  giggleBonus?: number
  /** 🪶 Featherweight (v2.93): tier 1's once-per-node pass is spent. */
  featherweightUsed?: boolean
  /** 🎭 Routine (v2.93): the copied statline worn until node end. */
  routineStats?: Stats
  /** 🎭 Routine Apotheosis: the copied passive worn with it. */
  routinePassive?: string
  /** 🌀 v6.91 Blackhole: the swallowed mission's requirements, worn as
   * bonus stats until his next deployment resolves. */
  blackholeStats?: Stats
  /** 🏗️ v6.92 Robodude's Hold Position: the site he stands on after his
   * mission (its POI index and board position) until dispatched again or
   * the node ends. Available while stationed; never in the home ring. */
  stationedPoi?: number
  stationedX?: number
  stationedY?: number
  /** 💧 Osmosis (v2.94): successes banked toward the next point. */
  osmosisCount?: number
  /** 🧊 Permafrost (v5.21): freeze-pool points spent this NODE. */
  permafrostUsed?: number
  /** 🌘 Eclipse Bloom (v5.33): where the rotating bloom stands. */
  eclipseBloomIndex?: number
  /** 🔥 Catchfire (v4.66): the once-per-node permanent burn-in spent. */
  giggleSpent?: boolean
  /** 🐻 Hibernal Might (v4.66): the once-per-node bank already paid. */
  hibernalBanked?: boolean
  /** 🌊 Tide Deluge (v4.66): spent surges layered up for this node. */
  tideStacks?: number
  /** 🪞 Who this Echopus fought beside this node (v4.86) — the
   * Apotheosis keeper banks only from these ids. Reset at node end. */
  reflectionPartners?: string[]
  /** 🌙 Moonlit Mending (v4.67): the 3-marble bag, drawn per success. */
  moonlitBag?: boolean[]
  /** 🗿 Bedrock Memory Apotheosis (v2.95): stat points ever spent. */
  spentStatPoints?: number
  /** 🦔 Thorn Toll priming — DEAD since v5.14 (the aura is always on);
   * kept only so pre-v5.14 saves parse. */
  thornTollPrimed?: boolean
  /** 🛡️ Bulwark (v3.00): seconds until the Sentinel recovers. */
  benchSeconds?: number
  /** 🪶 Understudy (v3.05): random trained skills already granted. */
  understudyGranted?: number
  /** 👑 Decree (v3.06): decrees issued this node. */
  decreeUses?: number
  /** 👑 Armed and waiting for the next eligible Fight (v3.56). Lives on
   * the creature, not in a `useState`, so a reload mid-node cannot lose
   * an order the player already gave. */
  decreeArmed?: boolean
  /** 🎭 Mimic (v3.05): a copied surge riding until its next mission. */
  mimicElements?: ElementKey[]
  mimicBonus?: number
  /** ✨ Luminesce (v3.01): its armed mission succeeded — the node's
   * next reward pick offers an extra card. */
  luminesceReady?: boolean
}


export type NodeType =
  | 'swarm' | 'mixed' | 'titan' | 'elite' | 'rest' | 'chest' | 'boss'
  /** Spend gold on up to 6 random relics; one per act (elite-style placement). */
  | 'shop'
  /** Unknown until entered; resolves to treasure, respite, an ambush,
   * or a puzzle. */
  | 'mystery'
  /** Combat a mystery node can turn into — never generated directly. */
  | 'ambush'
  /** All missions at once, mirrored from the player's roster; each
   * creature assigns once; reward quality scales with damage taken. */
  | 'puzzle'

export type EncounterTier = 'easy' | 'medium' | 'hard' | 'elite' | 'boss'

export interface EncounterPreview {
  requiredSuccesses: number
  focusElements: ElementKey[]
  tier: EncounterTier
  /** Boss nodes pre-roll their exact statline at map generation, so the
   * player can preview it and every (re)spawn is the same boss. */
  boss?: {
    /** Fixed boss name (older saves may lack it; respawns then re-roll). */
    name?: string
    required: Stats
    damage: number
    xp: number
    slots: number
    /** The boss's skill, rolled at map generation (data/bossSkills.ts).
     * Typed loosely to avoid a circular import. */
    skill?: string
    /** Pre-rolled target element for corrupted / feint (previewable). */
    skillElement?: ElementKey
  }
}

export interface MapNode {
  id: string
  row: number
  col: number
  type: NodeType
  /** Ids of connected nodes in the next row — or, for a TITAN (v5.42),
   * in the row AFTER next: beating the titan skips a row. */
  next: string[]
  /** null for non-combat nodes (rest, chest). */
  preview: EncounterPreview | null
  /** ⚔️ v5.42: elite nodes roll ONE modifier at map generation
   * (data/eliteModifiers.ts), previewed on the map. Absent on pre-v5.42
   * saves and non-elite nodes = classic behavior. */
  modifier?: string
  /** 👿 v5.66 Twin Curses (weekly modifier): the elite's SECOND curse.
   * Only ever set on twin-curse weeks; always differs from `modifier`. */
  modifier2?: string
}

export interface GameMap {
  /** Total rows across all acts; one continuous scrollable board. */
  rows: number
  /** Rows per act segment; act = floor(row / rowsPerAct) + 1. */
  rowsPerAct: number
  nodes: MapNode[]
}

export type MissionStatus =
  | 'waiting'
  /** Team is walking to the site; expire timer is stopped (dispatch commits). */
  | 'traveling'
  /** Team is fighting at the site (fight timer runs). */
  | 'assigned'
  | 'resolving' | 'succeeded' | 'failed' | 'expired'

/** Purely visual scenery scattered around the field map. */
export interface FieldDecoration {
  x: number
  y: number
  icon: string
}

/** A dispatched team walking home after its mission resolved. */
export interface ReturnTrip {
  id: string
  /** A failed assignment returning home; Homeward Star is spent on arrival. */
  starweaveFailed?: boolean
  creatureIds: string[]
  remaining: number
  total: number
  /** Path geometry back home (copied from the mission). */
  x: number
  y: number
  ctrlX: number
  ctrlY: number
  /** 🌲 Forest fields (v1.20): the mission's point of interest, so the
   * walk home retraces the painted trail. */
  poi?: number
}

export type MissionTier = 'easy' | 'medium' | 'hard' | 'boss'

export interface Mission {
  id: string
  name: string
  tier: MissionTier
  /** Field position (x in 0–100 units, y in board pixels). Missions can
   * appear anywhere; distance from home drives travel and danger. */
  x: number
  y: number
  /** 🌲 Forest fields (v1.20): index of this mission's point of interest
   * in the node's forest — teams walk the painted trail instead of a
   * generated road. Absent on boss nodes and pre-forest saves. */
  poi?: number
  /** Path control point, laid out when the team is dispatched. */
  ctrlX: number
  ctrlY: number
  distance: number
  /** Seconds of walking remaining / total for the outbound leg. */
  travelRemaining: number
  travelTotal: number
  /**
   * Seconds for the walk HOME, captured at dispatch (v3.37). Usually the
   * same as travelTotal, but the instant-deploy effects differ: 🥾
   * Trailblazer blazes only the trip out (so this holds the real walk),
   * while 💧 Skim ("out and back"), 👑 Decree and 🚀 Fast Deployment
   * cover both legs (0). Undefined on missions dispatched before v3.37 —
   * consumers fall back to travelTotal.
   */
  homeTravel?: number
  required: Stats
  /** Seconds left to assign creatures; only ticks while status is 'waiting'. */
  expireTimer: number
  expireTotal: number
  /** Seconds the dispatched team is busy; only ticks while status is 'assigned'. */
  fightTimer: number
  fightTotal: number
  damage: number
  xp: number
  slots: number
  assignedCreatureIds: string[]
  /** Success chance captured when the player clicked Fight; null while waiting. */
  assignedChance: number | null
  /** 🌄 v7.14: true when this mission was the node's FIRST dispatch -
   * snapshotted as it ships, so First Light resolves against the same
   * fact it previewed (the encounter-wide flag flips at that moment). */
  firstDispatch?: boolean
  /** Team Journal consumed on this dispatch: +10% at resolution. */
  journalBoosted?: boolean
  /** ⚔️ v5.42 elite modifiers: 😡 Enraged growth already applied (caps
   * at ENRAGE_CAP) and the seconds accrued toward the next +1. */
  enrageBonus?: number
  enrageTick?: number
  /** 🦈 Voracious: this mission already rose once — it stays down now. */
  voraciousReborn?: boolean
  /** 🎓 v7.20: which First Dispatch lesson this mission teaches. */
  tutorialLesson?: 'dispatch' | 'expire' | 'surge' | 'pair' | 'skill'
  /** 🚩 Warlord's Banner (v5.43): the elements this mission was taxed
   * on while the champion stood — refunded when the champion falls. */
  bannerTax?: ElementKey[]
  /** 🪙 Tithe (v2.92): this mission was dispatched under the tithe. */
  titheBoosted?: boolean
  /** 🌊 Deluge (v2.98): dispatched inside the open window. */
  delugeBoosted?: boolean
  /** 👑 Decree (v3.06): resolved by royal order — no travel, no fight. */
  decreed?: boolean
  /** 🎲 v6.88: Mr. Khool's Wager rode this mission (set at dispatch; see wagerSteps). */
  wagered?: boolean
  /** 🎩 v6.88: a Composure reroll turned this mission's failed roll into a win. */
  composureSaved?: boolean
  /** 🌀 v6.91: Lanfeust's Blackhole swallowed this dispatch - no roll, no
   * travel, no XP, gold or goal credit; its requirements become his next
   * deployment's bonus stats. */
  eaten?: boolean
  /** 🎯 v6.92 Robodude's Fire Support: this creature's full statline
   * counts for the mission without a slot, until it resolves. */
  fireSupportId?: string
  /** 🌫️ v5.66 Fog of War (weekly modifier): seconds until this
   * mission's demands surface. Undefined/0 = readable. */
  fogRemaining?: number
  /** 👑 The XP share Sovereign granted this decree (base 0). */
  decreeXpShare?: number
  /** 👑 Sovereign Apotheosis: this decree counts double toward ⭐. */
  decreedDouble?: boolean
  /** 🌞 Sundial (v2.82): dispatched with under 5s left — pays double
   * XP and gold on success. Captured at dispatch, since the timer stops
   * the moment the team commits. */
  sundialBoosted?: boolean
  /** 😤 Grudge consumed on this dispatch: +20% at resolution. */
  grudgeBoosted?: boolean
  /** 🩸 Blood Pact toggled for this dispatch: 2 HP paid for +10%. */
  bloodPactPaid?: boolean
  /** The boss skill this mission carries (bosses + fracture fragments). */
  bossSkill?: string
  /** Knockout skill: this creature can't join the fight. */
  knockedOutCreatureId?: string | null
  /** 😴 v5.27: the Sandman's sleepers, plural (count scales by ascension). */
  knockedOutCreatureIds?: string[]
  /** Corrupted Element: overcommitting this element punishes failure. */
  corruptedElement?: ElementKey
  /** Element Eclipse: the currently blocked element (rotates while the
   * player plans; locked in at dispatch). */
  eclipsedElement?: ElementKey
  /** Unstable Requirements: the element currently growing (rotates). */
  unstableElement?: ElementKey
  /** Unstable clocks (v0.50): tick on the field clock AND in real time
   * while the detail is open. Seconds until next growth / rotation. */
  unstableGrowIn?: number
  unstableRotateIn?: number
  /** Boss Feint: this requirement scatters onto the others at dispatch. */
  feintElement?: ElementKey
  /** How the feint landed (set at dispatch, shown at resolution). */
  feintScatter?: { from: ElementKey; points: number; gains: Partial<Record<ElementKey, number>> }
  /** Fracture/Yin&Yang: split generation (0 = the boss, 1+ = pieces). */
  fractureDepth?: number
  /** Boss minions (Dr. Vex's meddling / Golem crystals): never count
   * toward the node goal; expiring one chips the player. */
  minion?: 'scientist' | 'crystal'
  /** Faust's bargain: which curse the player accepted (1..3). */
  curseChoice?: 1 | 2 | 3
  /** Gold missions pay this on success and never count toward the goal. */
  goldReward?: number
  /** Puzzle-node mission: each creature may join only one of these. */
  puzzle?: boolean
  /** The creature group this trial was mirrored from — the "intended
   * answer" revealed in the end-of-node summary (v0.74). */
  puzzleAnswer?: string[]
  /** 🎓 Training mission (v0.79): one creature, no roll, long duration;
   * completing it grants the chosen trained skill. */
  training?: boolean
  /** Eligible trained skills on offer (three). */
  trainingOptions?: string[]
  /** The skill the player picked at dispatch. */
  trainingChoice?: string | null
  /** 🎓 v6.85 Broader Curriculum: a second lesson picked at dispatch, taught
   * alongside the first when the run's once-per-run double lesson is unused. */
  trainingSecondChoice?: string | null
  status: MissionStatus
  /** Seconds until a finished card disappears from the board; null while live. */
  removeIn: number | null
  /** 💢 v3.81: HP the player ACTUALLY lost when this mission lapsed —
   * stamped by the expiry paths, read by the board's float. The board used
   * to print `damage` (the full, on-failure number) for every expiry, so a
   * gold mission that costs nothing still flashed "−8 HP" and played the
   * hit sting (Javi's report), and an ordinary expiry overstated its bite
   * by 2× since expiries only deal `expiredDamageMult` of it. Zero here
   * means the lapse was free — no float, no sound. */
  expiryDamage?: number
}

export interface EncounterState {
  nodeId: string
  goal: number
  successes: number
  missions: Mission[]
  /** 🌲 Forest fields (v1.20): the painted forest this node plays on.
   * Rolled at node entry for every combat node EXCEPT bosses (they keep
   * the classic board). Absent on pre-forest saves → classic board. */
  forestId?: string
  /** 👑 The boss's entrance glide has played (v2.30) — set by the board
   * so re-entering the screen doesn't replay it. */
  bossEntranceDone?: boolean
  /** 🩹 Field Medics whose once-per-node max-HP growth already paid
   * (v4.65) — the meter that keeps fail-farming from minting infinite
   * max HP. */
  medicMaxHpPaid?: string[]
  /** 🚩 Warlord's Banner (v5.43): the champion has fallen — spawns stop
   * carrying the tax and waiting missions were refunded. */
  bannerLifted?: boolean
  /** Scenery for this encounter's field map. */
  decorations: FieldDecoration[]
  /** Teams walking home; creatures free up when they arrive. */
  returnTrips: ReturnTrip[]
  /** Regular mission clock. null = NOTHING spawns here (puzzle fields
   * lay every trial out up front). v1.12: deliberately null, NOT
   * Infinity — Infinity doesn't survive the JSON save round-trip, and a
   * reload on a puzzle node re-armed the clock (null - dt coerces to
   * negative) and dropped a normal mission onto the trial field. Same
   * footgun as the gold clock below (v0.88). */
  nextSpawnIn: number | null
  status: 'active' | 'complete'
  /** 🎓 v7.04 First Dispatch: which scripted beat arrives next, and the
   * seconds until it does (data/firstDispatch.ts). Absent outside the tutorial. */
  tutorialStep?: number
  tutorialClock?: number
  /** 🎓 v7.20: the mission dealt for the current lesson, how it ended, and
   * how many times the lesson has been dealt again (the "try again" coach). */
  tutorialMissionId?: string
  tutorialOutcome?: 'won' | 'lost' | 'expired' | 'discarded'
  tutorialRetries?: number
  /** 🎓 v8.02: the practice's inspector has been closed during the 'skill'
   * lesson (the sheet then teaches Skim instead of the glass). */
  tutorialInspected?: boolean
  /** Once-per-encounter effect consumption flags. */
  safetyNetUsed: boolean
  medicBandUsed: boolean
  perfectPlanUsed: boolean
  /** Fast Deployment: the first assignment per encounter skips travel. */
  fastDeployUsed: boolean
  /** 🕊️ v6.90 The Noble One's Silence: stacks of +1 to every element on
   * everyone, one per 10 quiet seconds (silenceClock), cleared by any
   * press - except the first each node at the Apotheosis (silenceSpared). */
  silenceStacks?: number
  silenceClock?: number
  silenceSpared?: boolean
  /** 🌌 v6.91 Lanfeust's Gravity: seconds banked toward the next pull. */
  gravityClock?: number
  /** Team Journal: a failure primes the next assignment with +10%. */
  teamJournalPrimed: boolean
  /** 📦 Quartermaster (v2.82): extra mission slots banked this node. */
  /** 🪙 Tithe (v2.92): the next dispatch carries +20% chance. */
  tithePrimed?: boolean
  /** 🧱 Bulwark Drop (v2.92): the next failure anywhere deals nothing. */
  bulwarkArmed?: boolean
  /** 🎭 Routine (v2.93): the most recently dispatched creature. */
  lastDispatchedId?: string
  /** 🌊 Deluge (v2.98): seconds left on the +25% window. */
  delugeSeconds?: number
  /** 🐲 Dragon's Hoard Apotheosis (v3.03): the node's peak gold. */
  goldHighWater?: number
  /** 🔥 Rekindle (v3.03): its once-per-node save is spent. */
  rekindleUsed?: boolean
  /** ♾️ Eternal Return (v3.04): each creature's stat totals at node
   * start, so the node's gains can be re-granted at its end. */
  statSnapshot?: Record<string, Stats>
  /** 🌄 First Light (v2.82): the node's first dispatch has gone out. */
  firstDispatchDone?: boolean
  /** 💨 Tailwind (v2.82): Speed banked by Tailwind creatures already
   * dispatched this node, applied to every later trip. */
  tailwindSpeed?: number
  /** Focus Bell speeds up the first 3 assignments per encounter. */
  focusBellUses: number
  /** Gold missions run on their own spawn clock, separate from combat.
   * null = NO gold on this field (boss/puzzle nodes). v0.88: null, not
   * Infinity — Infinity doesn't survive the JSON save round-trip and a
   * reload mid-boss-fight used to re-arm the clock (the Yin & Yang
   * "gold boss clone" bug). */
  nextGoldSpawnIn: number | null
  /** Boss minions (scientist/crystals) run on their own clock too. */
  nextMinionSpawnIn?: number
  /** Basalt: set after a lost fight — the crystal ring refills slowly. */
  golemSlowed?: boolean
  /** HP lost during this encounter — the puzzle node's "score". */
  damageTaken?: number
  /** Puzzle nodes: the generating splits, kept for the summary reveal
   * (trial missions get filtered away at completion). */
  puzzleAnswers?: { name: string; creatureIds: string[] }[]
  /** 🧩 v7.77 (per Javi: "puzzle success should be intended on if the split matched the optimal split, not
   * necessarily if damage was taken"): every trial's team added up to its intended group, element by element -
   * judged as the trials began. */
  puzzleSplitMatched?: boolean
  /** 🧩 v7.77: the node paid a relic (the split matched, or not a scratch) - the summary reads this. */
  puzzleRelic?: boolean
  /** ⏫ Seconds this node has been live (v4.72) — feeds the spawn-time
   * damage escalation that ends infinite Field-Medic camping. Absent on
   * older saves → clock starts at reload. */
  secondsElapsed?: number
  /** ⏫ The secondsElapsed reading when the enrage ARMED (v4.75: the
   * 5th regular spawn arms it). Unset = not armed yet; the tick stamps
   * it, so mid-camp saves re-arm on their first tick after load. */
  enrageAt?: number
  /** Missions spawned so far (for first-N timer relics). */
  spawnCount: number
  /** Gold missions spawned so far — its OWN counter (v2.17): sharing
   * spawnCount with the regular spawner let a gold spawn eat one of the
   * "first 5" Calm Hourglass slots and one opening-wave stagger slot
   * meant for regular missions. */
  goldSpawnCount?: number
  /** Creatures dispatched at least once this encounter. */
  everAssignedIds: string[]
  /** Creatures that completed at least one successful mission this encounter. */
  succeededIds: string[]
}

export type Rarity = 'Common' | 'Uncommon' | 'Rare' | 'Epic' | 'Legendary'

export type RewardPool = 'regular' | 'healing' | 'relic'

export interface RewardOffer {
  id: string
  /** Reward or relic definition id this card represents. */
  defId: string
  pool: RewardPool
}

/** A chosen reward that still needs a follow-up selection. */
export type RewardResolution =
  | { type: 'pick-creatures'; defId: string; count: number; xpEach: number; picked: string[] }
  | {
    type: 'recruit'
    /** Pre-rolled candidates: what you see is exactly what joins. */
    options: { defId: string; stats: Stats }[]
    level: number
  }
  | {
    /** 🎓 Training Ground (v0.84): pick 1 of 3 skills, then a student. */
    type: 'train'
    options: string[]
    chosen: string | null
  }

export interface ActLog {
  successes: number
  failures: number
  expiries: number
  damageTaken: number
  goldEarned: number
  /** Dispatch count per creature id — crowns the act's favorite. */
  dispatches: Record<string, number>
}

export interface ShopItem {
  relicId: string
  price: number
}

/** One freehand planning stroke; points in full-map board coordinates. */
export interface PlannedStroke {
  points: { x: number; y: number }[]
}

export type RunPhase = 'draft' | 'bonus' | 'summary' | 'map' | 'encounter'

export interface DraftState {
  /** 0-based index of the current pick (0..rosterSize-1). */
  pickIndex: number
  /** Creature def ids currently offered. */
  options: string[]
  /** Starting bonus ids offered after the last creature pick. */
  bonusOptions: string[]
  /** 🎓 v7.38 (F39): creature def ids picked so far (in pick order), so a
   * reload keeps an unfinished draft. normalizeRun defaults it to []. */
  selected?: string[]
}

export interface RunState {
  phase: RunPhase
  /** Purchased talents frozen at run start, with their saved use limits. */
  starweave?: StarweaveRunState
  /** 🏦 v4.92: unique per run — the wallet's idempotency key, so "act 3
   * cleared in run X" can only ever pay once. normalizeRun fills it. */
  runId?: string
  /** Epoch ms when the player hit Start Run (kept for the record). */
  startedAt: number
  /** Active play time in ms — only accrues while the tab is visible, so
   * minimizing the game freezes the run clock (v1.12). */
  playtimeMs: number
  /** True once the boss-reveal intro (after Start Run) has played. */
  bossIntroSeen: boolean
  /** Spendable gold (earned from gold missions; spent at shops). */
  gold: number
  /** Per-act stats for the between-acts summary screen (key = act). */
  actLog: Record<number, ActLog>
  /** Set when a boss falls: which act's summary to show on the map. */
  actSummary: number | null
  /** Rolled when entering a shop node; null otherwise. */
  shopStock: ShopItem[] | null
  /** Freehand route strokes drawn by the player (full-map coordinates). */
  plannedPath: PlannedStroke[]
  /** First-run tutorial active for this run (menu checkbox). */
  tutorial: boolean
  /** Tutorial step ids already shown (survives refresh). */
  tutorialSeen: string[]
  /** 🎓 v7.40 (per Javi, proposal §3 D): a FIRST RUN - a campaign run at
   * Ascension 0 started on a device (and, signed in, an account) with no
   * earlier play, or a restart of one. Stamped at start (utils/firstSteps.ts
   * startsFirstRun) so it rides the save and the cloud sync; ends (false)
   * when location 1 is cleared. The first-run rails key off it: only the
   * banner's ⏩ button toggles ×3 speed (v7.40), more in v7.41. Older saves,
   * and every other mode, normalize to false. */
  firstRun?: boolean
  /** 🎓 v7.41 (per Javi, proposal §3 C + D): what a FIRST RUN saw - board
   * seconds (and how many ran at ×3), health lost by cause and the elements
   * the team lacked where it lost health - for its kind defeat. Stamped with
   * `firstRun` at start and KEPT when location 1 falls: the rails (D7 enrage
   * at spawn 15, D8 easy-only) cover act-1 rows 0-1, so they key off it
   * (utils/firstRunRails.ts). Absent on every other run; normalizeRun cleans
   * it, gives a v7.40 first run an empty one and drops it outside campaigns. */
  firstRunTally?: import('~/utils/firstRunRails').FirstRunTally
  /** Ascension level this run plays on (0 = normal; 1–4 injury ladder). */
  ascension: number
  /** 🧩 Puzzle Game session (v1.28): set to the level being played.
   * Such a "run" is a throwaway shell around one puzzle encounter — the
   * player's real run is stashed and restored when the session ends. */
  puzzleLevel?: number
  /** Acts whose 🎓 Training mission has already spawned (once per act). */
  trainingActs?: number[]
  /** 🎭 v7.56: the face-off scenes this run has played (utils/versus.ts
   * keys: act1-open, act1-boss...), so a reload never replays one. */
  storyScenesSeen?: string[]
  /** 🔁 Offer decline-cycling (v5.17, per Javi): per-category ids the
   * player passed over. Rolls exclude these until the eligible pool has
   * cycled through, then the bag reshuffles — so a wanted relic, skill
   * or recruit can be target-farmed instead of dodged forever. */
  declinedOffers?: { relic: string[]; recruit: string[]; skill: string[] }
  /** Current act, 1-based; each act is a full map ending in a boss. */
  act: number
  /** 🗺️ Biome per act (v1.39): index act-1. Drawn without replacement
   * from the four themes at run start, so no theme repeats in a run. */
  actThemes?: import('~/data/forests').ForestTheme[]
  /** 🔥 Rekindle (v3.03): the act in which the once-per-ACT save fired. */
  rekindleActUsed?: number
  /** ♾️ Eternal Return Apotheosis (v3.04): node gains banked for the
   * act-end re-grant, per creature. */
  /** ♾️ DEAD since v5.30 (act-end bonus retired) — kept so pre-v5.30
   * saves parse; onActComplete just clears it. */
  eternalActGains?: Record<string, Stats>
  /** ⚔️ Gauntlet mode (v2.34): A10 rules, infinite acts, no victory. */
  gauntlet?: boolean
  /** 🎗️ v6.54: which keepsakes the ledger had unlocked when this run
   * began (never set in seeded modes), and which have had their one
   * offer this run. See data/keepsakes. */
  keepsakes?: { relic: boolean; skill: boolean }
  keepsakeOffered?: { relic?: boolean; skill?: boolean }
  /** 🧘 v6.54: what The Ascetic counts - surges spent on a mission,
   * creature skills armed or fired, recruits taken, gold spent. */
  surgesFired?: number
  skillsUsed?: number
  recruits?: number
  goldSpent?: number
  hp: number
  maxHp: number
  roster: Creature[]
  maxRosterSize: number
  /** Legacy: pre-relic starting-bonus id from old saves; always null now. */
  bonusId: string | null
  relics: string[]
  draft: DraftState | null
  map: GameMap | null
  /** Id of the last completed node the player stands on; null at the start. */
  position: string | null
  /** Node currently being attempted while phase is 'encounter'. */
  activeNodeId: string | null
  completedNodeIds: string[]
  bossDefeated: boolean
  /** 🏅 v3.62: stardust paid for clearing this mode for the FIRST time,
   * so the victory screen can call it out. Absent on a repeat clear. */
  firstClearBounty?: number
  /** 🔓 v7.79: the ascension this win unlocked for the FIRST time, so the victory banner announces only a real
   * unlock (per Javi). Absent when the account already had it. */
  newAscension?: number
  /** 🤫 A10 (v0.84): the hidden FOURTH boss has appeared on the map. */
  secretBossRevealed?: boolean
  /** 🌟 Rare creatures owned when this run started (v1.01) — snapshot
   * so recruits/draft stay stable even if purchases happen mid-run. */
  unlockedCreatures?: string[]
  /** True once HP hit 0 — the run is lost. */
  defeated: boolean
  /** Live combat state while phase is 'encounter'; null otherwise. */
  encounter: EncounterState | null
  /** Reward cards currently offered; null when none pending. */
  pendingRewards: RewardOffer[] | null
  /** Remaining reward stages after the current one (elite/boss get two). */
  pendingRewardPools: RewardPool[]
  /** Chosen reward awaiting a follow-up pick (creature/recruit). */
  rewardResolution: RewardResolution | null
  /** 🎲 v5.54 DAILY runs: 'daily' marks the seeded, own-slot mode
   * (absent/undefined = standard campaign; gauntlet stays its own
   * boolean). `seed` is the full seed string; `dailyDate` the UTC
   * YYYY-MM-DD the seed belongs to. */
  /** 🎓 v7.04: 'tutorial' is the First Dispatch - a guided throwaway run. */
  mode?: 'daily' | 'tutorial'
  seed?: string
  dailyDate?: string
  /** 🗓️⚗️ v5.66: the WEEKLY MODIFIER this run was dealt under — frozen
   * at start so a schedule edit never mutates an in-flight run.
   * Absent on campaign/gauntlet runs and vanilla weeks. */
  weeklyMod?: string
  /** 🎓 v5.56 DAILY runs: the day's teaching curriculum — a
   * seed-shuffled deck of trained-skill ids rolled at run start. Every
   * teaching surface deals the first TEACHABLE entries in deck order
   * (offered entries rotate to the back), so the school's offers are
   * part of the skeleton. Absent on campaign runs (they keep the
   * synergy-nudged live roll). */
  trainingDeck?: string[]
  /** 🎓 v5.86: the trained skills THIS run may teach — frozen at
   * start from the account's ascension unlocks (Javi's ladder).
   * Absent = full set (weekly runs, and saves from before this). */
  skillPool?: string[]
  /** ❓ v5.39: narrative event revealed by a mystery node — the event id
   * awaiting the player's choice; null/undefined when none. Same
   * lifetime rules as pendingRewards. */
  pendingEvent?: string | null
  /** ❓ v7.16: the pending event's choice has landed (reward applied and
   * saved) and only the modal's exit animation remains - a reload in that
   * window finishes the node instead of offering the event again. */
  pendingEventResolved?: boolean
  /** Events already drawn this run (per-group bags reshuffle when a
   * group runs dry — see rollEvent). */
  seenEvents?: string[]
}
